import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Menu, Grid } from 'antd';
import IntlMessage from '../util-components/IntlMessage';
import Icon from '../util-components/Icon';
import navigationConfig from 'configs/NavigationConfig';
import { connect, useSelector } from 'react-redux';
import { SIDE_NAV_LIGHT, NAV_TYPE_SIDE } from 'constants/ThemeConstant';
import utils from 'utils';
import { onMobileNavToggle } from 'redux/actions/Theme';
import privilegeMap from 'configs/AccessControlConfig';

const { SubMenu } = Menu;
const { useBreakpoint } = Grid;

const setLocale = (isLocaleOn, localeKey) =>
  isLocaleOn ? <IntlMessage id={localeKey} /> : localeKey.toString();

const setDefaultOpen = (key) => {
  let keyList = [];
  let keyString = '';
  if (key) {
    const arr = key.split('-');
    for (let index = 0; index < arr.length; index++) {
      const elm = arr[index];
      index === 0 ? (keyString = elm) : (keyString = `${keyString}-${elm}`);
      keyList.push(keyString);
    }
  }
  return keyList;
};

const SideNavContent = (props) => {
  const {
    sideNavTheme,
    hideGroupTitle,
    localization,
    onMobileNavToggle,
    routeInfo,
  } = props;
  const [content, setContent] = useState([...navigationConfig]);
  const { profile } = useSelector((state) => state.auth);
  const navigate = useNavigate();

  const isMobile = !utils.getBreakPoint(useBreakpoint()).includes('lg');
  const closeMobileNav = () => {
    if (isMobile) {
      onMobileNavToggle(false);
    }
  };

  const handleMenuClick = (path) => {
    if (path) {
      navigate(path);
      closeMobileNav();
    }
  };

  useEffect(() => {
    let navContent = [...navigationConfig];

    //Retrieve all keys from privilegeMap
    const allKeys = privilegeMap.reduce((acc, item) => {
      return [...acc, ...item.keys];
    }, []);

    // Disable all menus and submenus that its key matches privilegeMap keys
    const disableMenus = (menus) => {
      menus.forEach((menu) => {
        if (allKeys.includes(menu.key)) {
          menu.isEnabled = false;
        }
        if (menu.submenu && menu.submenu.length > 0) {
          disableMenus(menu.submenu);
        }
      });
    };

    if (navContent[0] && navContent[0].submenu) {
      disableMenus(navContent[0].submenu);
    }

    const enableMenu = (key, menuContent) => {
      const menu = menuContent.find((item) => item.key === key);
      if (menu) menu.isEnabled = true;
    };

    const mainMenu = navContent.find(
      (menu) => menu.key === 'main-menu',
    ).submenu;

    privilegeMap.forEach(({ privilege, keys }) => {
      let currentMenu = mainMenu;
      if (utils.checkUserPrivileges(privilege) && privilege !== 'All') {
        keys.forEach((key) => {
          enableMenu(key, currentMenu);
          currentMenu =
            currentMenu.find((menu) => menu.key === key)?.submenu || [];
        });
      } else if (privilege === 'All') {
        // Enable menu that all privileges are allowed
        keys.forEach((key) => {
          enableMenu(key, mainMenu);
          currentMenu =
            currentMenu.find((menu) => menu.key === key)?.submenu || [];
        });
      }
    });
    setContent(navContent);
  }, [profile]);

  return (
    <Menu
      theme={sideNavTheme === SIDE_NAV_LIGHT ? 'light' : 'dark'}
      mode="inline"
      style={{ height: '100%', borderRight: 0 }}
      defaultSelectedKeys={[routeInfo?.key]}
      defaultOpenKeys={setDefaultOpen(routeInfo?.key)}
      className={hideGroupTitle ? 'hide-group-title' : ''}
      data-testing="MenuContent-SideNav-Main"
      id="menuContent-sideNav-main"
    >
      {content.map((menu) => {
        if (menu.isEnabled) {
          return menu.submenu.length > 0 ? (
            <Menu.ItemGroup
              key={menu.key}
              title={setLocale(localization, menu.title)}
              data-testing={`MenuContent-ItemGroup-${menu.key}`}
              id={`menuContent-itemGroup-${menu.key}`}
            >
              {menu.submenu.map((subMenuFirst) => {
                if (subMenuFirst.isEnabled) {
                  return subMenuFirst.submenu.length > 0 ? (
                    <SubMenu
                      icon={
                        subMenuFirst.icon ? (
                          <Icon
                            type={subMenuFirst?.icon}
                            id={`menuContent-icon-${subMenuFirst.key}`}
                          />
                        ) : null
                      }
                      key={subMenuFirst.key}
                      title={
                        <span id={`menuContent-title-${subMenuFirst.key}`}>
                          {setLocale(localization, subMenuFirst.title)}
                        </span>
                      }
                      data-testing={`MenuContent-SubMenu-${subMenuFirst.key}`}
                      id={`menuContent-subMenu-${subMenuFirst.key}`}
                    >
                      {subMenuFirst.submenu.map((subMenuSecond) => {
                        if (subMenuSecond.isEnabled) {
                          return subMenuSecond.submenu.length > 0 ? (
                            <SubMenu
                              icon={
                                subMenuSecond.icon ? (
                                  <Icon
                                    type={subMenuSecond?.icon}
                                    id={`menuContent-icon-${subMenuSecond.key}`}
                                  />
                                ) : null
                              }
                              key={subMenuSecond.key}
                              title={
                                <span
                                  id={`menuContent-title-${subMenuSecond.key}`}
                                >
                                  {setLocale(localization, subMenuSecond.title)}
                                </span>
                              }
                              data-testing={`MenuContent-SubMenu-${subMenuSecond.key}`}
                              id={`menuContent-subMenu-${subMenuSecond.key}`}
                            >
                              {subMenuSecond.submenu.map((subMenuThird) => {
                                if (subMenuThird.isEnabled) {
                                  return (
                                    <Menu.Item
                                      key={subMenuThird.key}
                                      onClick={() =>
                                        handleMenuClick(subMenuThird.path)
                                      }
                                      data-testing={`MenuContent-MenuItem-${subMenuThird.key}`}
                                      id={`menuContent-menuItem-${subMenuThird.key}`}
                                    >
                                      {subMenuThird.icon ? (
                                        <Icon
                                          type={subMenuThird?.icon}
                                          id={`menuContent-icon-${subMenuThird.key}`}
                                        />
                                      ) : null}
                                      <span
                                        id={`menuContent-span-${subMenuThird.key}`}
                                      >
                                        {setLocale(
                                          localization,
                                          subMenuThird.title,
                                        )}
                                      </span>
                                    </Menu.Item>
                                  );
                                }
                                return null;
                              })}
                            </SubMenu>
                          ) : (
                            <Menu.Item
                              key={subMenuSecond.key}
                              onClick={() =>
                                handleMenuClick(subMenuSecond.path)
                              }
                              data-testing={`MenuContent-MenuItem-${subMenuSecond.key}`}
                              id={`menuContent-menuItem-${subMenuSecond.key}`}
                            >
                              {subMenuSecond.icon ? (
                                <Icon
                                  type={subMenuSecond.icon}
                                  id={`menuContent-icon-${subMenuSecond.key}`}
                                />
                              ) : null}
                              <span
                                id={`menuContent-span-${subMenuSecond.key}`}
                              >
                                {setLocale(localization, subMenuSecond.title)}
                              </span>
                            </Menu.Item>
                          );
                        }
                        return null;
                      })}
                    </SubMenu>
                  ) : (
                    <Menu.Item
                      key={subMenuFirst.key}
                      onClick={() => handleMenuClick(subMenuFirst.path)}
                      data-testing={`MenuContent-MenuItem-${subMenuFirst.key}`}
                      id={`menuContent-menuItem-${subMenuFirst.key}`}
                    >
                      {subMenuFirst.icon ? (
                        <Icon
                          type={subMenuFirst.icon}
                          id={`menuContent-icon-${subMenuFirst.key}`}
                        />
                      ) : null}
                      <span id={`menuContent-span-${subMenuFirst.key}`}>
                        {setLocale(localization, subMenuFirst.title)}
                      </span>
                    </Menu.Item>
                  );
                }
                return null;
              })}
            </Menu.ItemGroup>
          ) : (
            <Menu.Item
              key={menu.key}
              onClick={() => handleMenuClick(menu.path)}
              data-testing={`MenuContent-MenuItem-${menu.key}`}
              id={`menuContent-menuItem-${menu.key}`}
            >
              {menu.icon ? (
                <Icon type={menu?.icon} id={`menuContent-icon-${menu.key}`} />
              ) : null}
              <span id={`menuContent-span-${menu.key}`}>
                {setLocale(localization, menu?.title)}
              </span>
            </Menu.Item>
          );
        }
        return null;
      })}
    </Menu>
  );
};

const TopNavContent = (props) => {
  const { topNavColor, localization } = props;
  const navigate = useNavigate();

  const handleMenuClick = (path) => {
    if (path) {
      navigate(path);
    }
  };
  return (
    <Menu
      mode="horizontal"
      style={{ backgroundColor: topNavColor }}
      data-testing="MenuContent-TopNav-Main"
      id="menuContent-topNav-main"
    >
      {navigationConfig.map((menu) =>
        menu.submenu.length > 0 ? (
          <SubMenu
            key={menu.key}
            popupClassName="top-nav-menu"
            title={
              <span id={`menuContent-topNav-span-${menu.key}`}>
                {menu.icon ? (
                  <Icon
                    type={menu?.icon}
                    id={`menuContent-topNav-icon-${menu.key}`}
                  />
                ) : null}
                <span id={`menuContent-topNav-title-${menu.key}`}>
                  {setLocale(localization, menu.title)}
                </span>
              </span>
            }
            data-testing={`MenuContent-TopNav-SubMenu-${menu.key}`}
            id={`menuContent-topNav-subMenu-${menu.key}`}
          >
            {menu.submenu.map((subMenuFirst) =>
              subMenuFirst.submenu.length > 0 ? (
                <SubMenu
                  key={subMenuFirst.key}
                  icon={
                    subMenuFirst.icon ? (
                      <Icon
                        type={subMenuFirst?.icon}
                        id={`menuContent-topNav-icon-${subMenuFirst.key}`}
                      />
                    ) : null
                  }
                  title={
                    <span id={`menuContent-topNav-title-${subMenuFirst.key}`}>
                      {setLocale(localization, subMenuFirst.title)}
                    </span>
                  }
                  data-testing={`MenuContent-TopNav-SubMenu-${subMenuFirst.key}`}
                  id={`menuContent-topNav-subMenu-${subMenuFirst.key}`}
                >
                  {subMenuFirst.submenu.map((subMenuSecond) => (
                    <Menu.Item
                      key={subMenuSecond.key}
                      onClick={() => handleMenuClick(subMenuSecond.path)}
                      data-testing={`MenuContent-TopNav-MenuItem-${subMenuSecond.key}`}
                      id={`menuContent-topNav-menuItem-${subMenuSecond.key}`}
                    >
                      <span id={`menuContent-topNav-span-${subMenuSecond.key}`}>
                        {setLocale(localization, subMenuSecond.title)}
                      </span>
                    </Menu.Item>
                  ))}
                </SubMenu>
              ) : (
                <Menu.Item
                  key={subMenuFirst.key}
                  onClick={() => handleMenuClick(subMenuFirst.path)}
                  data-testing={`MenuContent-TopNav-MenuItem-${subMenuFirst.key}`}
                  id={`menuContent-topNav-menuItem-${subMenuFirst.key}`}
                >
                  {subMenuFirst.icon ? (
                    <Icon
                      type={subMenuFirst?.icon}
                      id={`menuContent-topNav-icon-${subMenuFirst.key}`}
                    />
                  ) : null}
                  <span id={`menuContent-topNav-span-${subMenuFirst.key}`}>
                    {setLocale(localization, subMenuFirst.title)}
                  </span>
                </Menu.Item>
              ),
            )}
          </SubMenu>
        ) : (
          <Menu.Item
            key={menu.key}
            onClick={() => handleMenuClick(menu.path)}
            data-testing={`MenuContent-TopNav-MenuItem-${menu.key}`}
            id={`menuContent-topNav-menuItem-${menu.key}`}
          >
            {menu.icon ? (
              <Icon
                type={menu?.icon}
                id={`menuContent-topNav-icon-${menu.key}`}
              />
            ) : null}
            <span id={`menuContent-topNav-span-${menu.key}`}>
              {setLocale(localization, menu?.title)}
            </span>
          </Menu.Item>
        ),
      )}
    </Menu>
  );
};

const MenuContent = (props) => {
  return props.type === NAV_TYPE_SIDE ? (
    <SideNavContent {...props} />
  ) : (
    <TopNavContent {...props} />
  );
};

const mapStateToProps = ({ theme }) => {
  const { sideNavTheme, topNavColor } = theme;
  return { sideNavTheme, topNavColor };
};

export default connect(mapStateToProps, { onMobileNavToggle })(MenuContent);
